import { useEffect, useRef, useCallback } from 'react';

/**
 * GoogleSignInButton
 *
 * Renders an official Google-branded sign-in button using the
 * Google Identity Services (GSI) library.
 *
 * Props:
 *  - onSuccess(credential): called with the Google ID token string on success
 *  - onError(err):          called if sign-in fails (optional)
 *  - text:                  button label variant — 'signin_with' | 'signup_with' | 'continue_with'
 */
const GoogleSignInButton = ({ onSuccess, onError, text = 'signin_with' }) => {
  const buttonRef = useRef(null);
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  const handleCredentialResponse = useCallback(
    async (response) => {
      try {
        await onSuccess(response.credential);
      } catch (err) {
        onError?.(err);
      }
    },
    [onSuccess, onError]
  );

  const initializeGSI = useCallback(() => {
    if (!window.google || !buttonRef.current) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredentialResponse,
      ux_mode: 'popup',
    });

    window.google.accounts.id.renderButton(buttonRef.current, {
      type: 'standard',
      shape: 'rectangular',
      theme: 'outline',
      text,
      size: 'large',
      logo_alignment: 'center',
      width: Math.min(buttonRef.current.offsetWidth || 400, 400),
    });
  }, [clientId, text, handleCredentialResponse]);

  useEffect(() => {
    if (window.google) {
      initializeGSI();
      return;
    }

    // Load the GSI script if not already present
    if (!document.getElementById('google-gsi-script')) {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGSI;
      document.body.appendChild(script);
    } else {
      // Script tag exists but may still be loading — wait for it
      const existing = document.getElementById('google-gsi-script');
      existing.addEventListener('load', initializeGSI);
      return () => existing.removeEventListener('load', initializeGSI);
    }
  }, [initializeGSI]);

  return (
    <div className="w-full flex justify-center">
      <div ref={buttonRef} className="w-full" />
    </div>
  );
};

export default GoogleSignInButton;
